import {checkLabelValueObject, isLabelValue} from '../utils/checkObjects';
import {EMPTY_LABEL_VALUE} from '../utils/constants';

/**
 * A class representing a Material.
 */
export class Material {
    /**
     * Create a material.
     * @param {number} id - The id value.
     * @param {string} name - The name value.
     * @param {string} description - The description value.
     * @param {string} quantity - The quantity value.
     * @param {string} unit - The unit value.
     * @param {string} baseUnit - The baseUnit value
     * @param {string} price - The price value.
     * @param {LabelValue} currency - The currency value.
     * @param {string} per - The per value.
     * @param {LabelValue} taxCode - The tax code value.
     * @param {string} tax - The tax value.
     * @param {string} l1Category - The l1 category value.
     * @param {string} l3Category - The l3 category value.
     * @param {string} requestedDeliveryDate - The requested delivery date value.
     * @param {LabelValue} plant - The plant value.
     * @param {LabelValue} storageLocation - The storage location value.
     * @param {DeliveryAddress} deliveryAddress - The delivery address value.
     * @param {string} totalPrice - The total price value.
     * @param {boolean} freeItem - If the item is free, or not
     * @param {boolean} transportCosts - If the item corresponds to transport costs, or not
     * @param {string} supplierPartId - The supplier part ID value
     * @param {string} supplierPartAuxiliaryId - The supplier part auxiliary ID value
     * @param {boolean} arePriceDataFromST - True whether is the price data (unit, price, currency and per) are coming from a sourcing tool (ST), false otherwise
     * @param {string} supplierComments - The supplierComments value.
     * @param {string} internalComments - The internalComments value.
     * @param {string} exchangeCurrencyRate - The exchangeCurrencyRate value.
     * @param {string} type - The type value.
     * @param {string} supplierStatus - The supplier status value.
     * @param {string} pslId - The PSL ID value.
     * @param {number} transportCostType - The transport Cost type (1- same supplier, 2 - different supplier, 3 - via condition type)
     * @param {array} transportCostConditions - The transport Cost conditions that where set on the material
     * @param {string} uniqueSourcingToolId - The unique id representing the material on the sourcing tool
     * @param {string} ppmCostPlan - The unique id representing the material on the sourcing tool
     * @param {string} taxAmount - The tax amount value received from Vertex
     * @param {LabelValue} source - The source of the material
     */
    constructor(
        id,
        name,
        description,
        quantity,
        unit,
        baseUnit,
        price,
        currency,
        per,
        taxCode,
        tax,
        l1Category,
        l3Category,
        requestedDeliveryDate,
        plant,
        storageLocation,
        deliveryAddress,
        totalPrice,
        freeItem,
        transportCosts,
        supplierPartId,
        supplierPartAuxiliaryId,
        arePriceDataFromST,
        supplierComments,
        internalComments,
        exchangeCurrencyRate,
        type,
        supplierStatus,
        pslId,
        transportCostType,
        transportCostConditions,
        uniqueSourcingToolId,
        ppmCostPlan,
        taxAmount,
        source,
    ) {
        this._id = id;
        this._name = name;
        this._description = description;
        this._quantity = quantity;
        this._unit = unit;
        this._baseUnit = baseUnit;
        this._price = price;
        this._currency = checkLabelValueObject(currency);
        this._exchangeCurrencyRate = exchangeCurrencyRate || 1.0;
        this._per = per;
        this._taxCode = taxCode;
        this._tax = tax;
        this._l1Category = l1Category;
        this._l3Category = l3Category;
        this._requestedDeliveryDate = requestedDeliveryDate;
        this._plant = typeof plant === 'object' ? plant : EMPTY_LABEL_VALUE;
        this._storageLocation = typeof storageLocation === 'object' ? storageLocation : EMPTY_LABEL_VALUE;
        this._deliveryAddress = typeof deliveryAddress === 'object' ? deliveryAddress : {};
        this._totalPrice = totalPrice;
        this._freeItem = freeItem;
        this._transportCosts = transportCosts || false;
        this._supplierPartId = supplierPartId;
        this._supplierPartAuxiliaryId = supplierPartAuxiliaryId;
        this._arePriceDataFromST = arePriceDataFromST;
        this._supplierComments = supplierComments || '';
        this._internalComments = internalComments || '';
        this._type = type;
        this._supplierStatus = supplierStatus;
        this._pslId = pslId;
        this._transportCostType = transportCostType || 0;
        this._transportCostConditions = transportCostConditions || [];
        this._uniqueSourcingToolId = uniqueSourcingToolId || '';
        this._ppmCostPlan = ppmCostPlan || '';
        this._taxAmount = taxAmount || '';
        this._source = isLabelValue(source) ? source : EMPTY_LABEL_VALUE;
    }

    /**
     * Get an JS object containing all the information about the material.
     * @return {{quantity: string, description: string, storageLocation: ({label: string, value: string}|LabelValue|*), tax: string, requestedDeliveryDate: string, taxCode: LabelValue, l1Category: string, unit: string, total: string, deliveryAddress: ({}|DeliveryAddress|*), price: string, plant: ({label: string, value: string}|LabelValue|*), name: string, currency: {label: string, value: string}, id: number, per: string, l3Category: string, supplierComments: string, internalComments: string, transportCostType: number, transportCostConditions : array, materialSource: LabelValue}} The material.
     */
    getMaterial() {
        return {
            id: this._id,
            name: this._name,
            description: this._description,
            quantity: this._quantity,
            unit: this._unit,
            baseUnit: this._baseUnit,
            price: this._price,
            currency: this._currency,
            exchangeCurrencyRate: this._exchangeCurrencyRate,
            per: this._per,
            taxCode: this._taxCode,
            tax: this._tax,
            total: this._totalPrice,
            l1Category: this._l1Category,
            l3Category: this._l3Category,
            requestedDeliveryDate: this._requestedDeliveryDate,
            plant: this._plant,
            storageLocation: this._storageLocation,
            deliveryAddress: this._deliveryAddress,
            freeItem: this._freeItem,
            transportCosts: this._transportCosts,
            supplierPartId: this._supplierPartId,
            supplierPartAuxiliaryId: this._supplierPartAuxiliaryId,
            arePriceDataFromST: this._arePriceDataFromST,
            supplierComments: this._supplierComments,
            internalComments: this._internalComments,
            type: this._type,
            supplierStatus: this._supplierStatus,
            pslId: this._pslId,
            transportCostType: this._transportCostType,
            transportCostConditions: this._transportCostConditions,
            uniqueSourcingToolId: this._uniqueSourcingToolId,
            ppmCostPlan: this._ppmCostPlan,
            taxAmount: this._taxAmount,
            source: this._source,
        };
    }

    /**
     * Allow to set a material property from its name.
     * @param {string} name The name of the property.
     * @param {string | object} value The value to set.
     */
    setMaterial(name, value) {
        this['_' + name] = value;
    }

    /**
     * Calculate the total price of the material.
     * @param {Material} material
     * @return {number} The total price.
     */
    static computeTotalPrice(material) {
        return material.per && !material.freeItem ? material.price * material.quantity / material.per : 1.00;
    };

    /**
     * Get the id value.
     * @return {number} The id value.
     */
    get id() {
        return this._id;
    }

    /**
     * Get the name value.
     * @return {string} The name value.
     */
    get name() {
        return this._name;
    }

    /**
     * Get the description value.
     * @return {string} The description value.
     */
    get description() {
        return this._description;
    }

    /**
     * Get the quantity value.
     * @return {string} The quantity value.
     */
    get quantity() {
        return this._quantity;
    }

    /**
     * Set the quantity value.
     * @param {string} value - The quantity value.
     */
    set quantity(value) {
        this._quantity = value;
    }

    /**
     * Get the unit value.
     * @return {string} The unit value.
     */
    get unit() {
        return this._unit;
    }

    /**
     * Get the baseUnit value.
     * @return {string} The baseUnit value.
     */
    get baseUnit() {
        return this._baseUnit;
    }

    /**
     * Get the price value.
     * @return {string} The price value.
     */
    get price() {
        return this._price;
    }

    /**
     * Get the currency value.
     * @return {LabelValue} The currency value.
     */
    get currency() {
        return this._currency;
    }

    /**
     * Set the currency value.
     * @param {LabelValue} value - The currency value.
     */
    set currency(value) {
        this._currency = value;
    }

    /**
     * Get the per value.
     * @return {string} The per value.
     */
    get per() {
        return this._per;
    }

    /**
     * Get the tax code value.
     * @return {LabelValue} The tax code value.
     */
    get taxCode() {
        return this._taxCode;
    }

    /**
     * Set the tax code value.
     * @param {LabelValue} value - The tax code value.
     */
    set taxCode(value) {
        this._taxCode = value;
    }

    /**
     * Get the tax value.
     * @return {string} The tax value.
     */
    get tax() {
        return this._tax;
    }

    /**
     * Get the l1 category value.
     * @return {string} The l1 category value.
     */
    get l1Category() {
        return this._l1Category;
    }

    /**
     * Get the l3 category value.
     * @return {string} The l3 category value.
     */
    get l3Category() {
        return this._l3Category;
    }

    /**
     * Get the requested delivery date value.
     * @return {string} The requested delivery date value.
     */
    get requestedDeliveryDate() {
        return this._requestedDeliveryDate;
    }

    /**
     * Get the plant value.
     * @return {LabelValue} The plant value.
     */
    get plant() {
        return this._plant;
    }

    /**
     * Get the storage location value.
     * @return {LabelValue} The storage location value.
     */
    get storageLocation() {
        return this._storageLocation;
    }

    /**
     * Get the delivery address value.
     * @return {DeliveryAddress} The delivery address value.
     */
    get deliveryAddress() {
        return this._deliveryAddress;
    }

    /**
     * Get the total price value.
     * @return {string} The total price value.
     */
    get totalPrice() {
        return this._totalPrice;
    }

    /**
     * Get the freeItem value.
     * @return {boolean} The freeItem value.
     */
    get freeItem() {
        return this._freeItem;
    }

    /**
     * Get the transportCosts value.
     * @return {boolean} The transportCosts value.
     */
    get transportCosts() {
        return this._transportCosts;
    }

    /**
     * Get the transportCostType value.
     * @return {number} The transportCostType value.
     */
    get transportCostType() {
        return this._transportCostType;
    }

    /**
     * Get the transportCostConditions values.
     * @return {array} The transportCostConditions values.
     */
    get transportCostConditions() {
        return this._transportCostConditions;
    }

    /**
     * Set the id value.
     * @param {number} value - The id value.
     */
    set id(value) {
        this._id = value;
    }

    /**
     * Get the supplier Part ID value.
     * @return {string} The supplier Part ID value.
     */
    get supplierPartId() {
        return this._supplierPartId;
    }

    /**
     * Get the supplier Part Auxiliary ID value.
     * @return {string} The supplier Part Auxiliary ID value.
     */
    get supplierPartAuxiliaryId() {
        return this._supplierPartAuxiliaryId;
    }

    /**
     * Get the are price data from sourcing tool value.
     * @return {boolean} The are price data from sourcing tool value.
     */
    get arePriceDataFromST() {
        return this._arePriceDataFromST;
    }

    /**
     * Set the name value.
     * @param {string} value - The name value.
     */
    set name(value) {
        this._name = value;
    }

    /**
     * Set the description value.
     * @param {string} value - The description value.
     */
    set description(value) {
        this._description = value;
    }

    /**
     * Set the unit value.
     * @param {string} value - The unit value.
     */
    set unit(value) {
        this._unit = value;
    }

    /**
     * Set the baseUnit value.
     * @param {string} value - The baseUnit value.
     */
    set baseUnit(value) {
        this._baseUnit = value;
    }

    /**
     * Set the price value.
     * @param {number} value - The price value.
     */
    set price(value) {
        this._price = value;
    }

    /**
     * Set the per value.
     * @param {number} value - The per value.
     */
    set per(value) {
        this._per = value;
    }

    /**
     * Set the l1Category value.
     * @param {string} value - The l1Category value.
     */
    set l1Category(value) {
        this._l1Category = value;
    }

    /**
     * Set the l3Category value.
     * @param {string} value - The l3Category value.
     */
    set l3Category(value) {
        this._l3Category = value;
    }

    /**
     * Set the requestedDeliveryDate value.
     * @param {string} value - The requestedDeliveryDate value.
     */
    set requestedDeliveryDate(value) {
        this._requestedDeliveryDate = value;
    }

    /**
     * Set the plant value.
     * @param {LabelValue} value - The plant value.
     */
    set plant(value) {
        this._plant = value;
    }

    /**
     * Set the storageLocation value.
     * @param {LabelValue} value - The storageLocation value.
     */
    set storageLocation(value) {
        this._storageLocation = value;
    }

    /**
     * Set the deliveryAddress value.
     * @param {DeliveryAddress} value - The deliveryAddress value.
     */
    set deliveryAddress(value) {
        this._deliveryAddress = value;
    }

    /**
     * Set the totalPrice value.
     * @param {string} value - The totalPrice value.
     */
    set totalPrice(value) {
        this._totalPrice = value;
    }

    /**
     * Set the freeItem value.
     * @param {boolean} value - The freeItem value.
     */
    set freeItem(value) {
        this._freeItem = value;
    }

    /**
     * Set the transportCosts value.
     * @param {boolean} value - The transportCosts value.
     */
    set transportCosts(value) {
        this._transportCosts = value;
    }

    /**
     * Set the transportCostType value.
     * @param {number} value - The transportCostType value.
     */
    set transportCostType(value) {
        this._transportCostType = value;
    }

    /**
     * Set the transportCostConditions values.
     * @param {array} value - The transportCostConditions values.
     */
    set transportCostConditions(value) {
        this._transportCostConditions = value;
    }

    /**
     * Set the supplier part ID value.
     * @param {string} value - The supplier part ID value.
     */
    set supplierPartId(value) {
        this._supplierPartId = value;
    }

    /**
     * Set the supplier part auxiliary ID value.
     * @param {string} value - The supplier part auxiliary ID value.
     */
    set supplierPartAuxiliaryId(value) {
        this._supplierPartAuxiliaryId = value;
    }

    /**
     * Set the are price data from sourcing tool value.
     * @param {boolean} value - The are price data from sourcing tool value.
     */
    set arePriceDataFromST(value) {
        this._arePriceDataFromST = value;
    }

    /**
     * Get the supplierComments value.
     * @return {string} The supplierComments value.
     */
    get supplierComments() {
        return this._supplierComments;
    }

    /**
     * Set the supplierComments value.
     * @param {string} value - The supplierComments value.
     */
    set supplierComments(value) {
        this._supplierComments = value;
    }

    /**
     * Get the internalComments value.
     * @return {string} The internalComments value.
     */
    get internalComments() {
        return this._internalComments;
    }

    /**
     * Set the internalComments value.
     * @param {string} value - The internalComments value.
     */
    set internalComments(value) {
        this._internalComments = value;
    }

    /**
     * Get the currency rate exchange
     * @return {number}
     */
    get exchangeCurrencyRate() {
        return this._exchangeCurrencyRate;
    }

    /**
     * Set the new currency rate exchange
     * @param {number} value
     */
    set exchangeCurrencyRate(value) {
        this._exchangeCurrencyRate = value;
    }

    /**
     * Get the type value.
     * @return {string} The type value.
     */
    get type() {
        return this._type;
    }

    /**
     * Set the type value.
     * @param {string} value - The type value.
     */
    set type(value) {
        this._type = value;
    }

    /**
     * Get the supplier status value.
     * @return {string} The supplier status value.
     */
    get supplierStatus() {
        return this._supplierStatus;
    }

    /**
     * Set the supplier status value.
     * @param {string} value - The supplier status value.
     */
    set supplierStatus(value) {
        this._supplierStatus = value;
    }

    /**
     * Get the PSL ID value.
     * @return {string} The PSL ID value.
     */
    get pslId() {
        return this._pslId;
    }

    /**
     * Set the PSL ID value.
     * @param {string} value - The PSL ID value.
     */
    set pslId(value) {
        this._pslId = value;
    }

    /**
     * Get the unique sourcing tool id.
     * @return {string} The unique sourcing tool id.
     */
    get uniqueSourcingToolId() {
        return this._uniqueSourcingToolId;
    }

    /**
     * Set the unique sourcing tool id.
     * @param {string} value - The unique sourcing tool id.
     */
    set uniqueSourcingToolId(value) {
        this._uniqueSourcingToolId = value;
    }

    /**
     * Get the PPM Cost Plan.
     * @return {string} The PPM Cost Plan.
     */
    get ppmCostPlan() {
        return this._ppmCostPlan;
    }

    /**
     * Set the PPM Cost Plan.
     * @param {string} value - The PPM Cost Plan.
     */
    set ppmCostPlan(value) {
        this._ppmCostPlan = value;
    }

    /**
     * Get the Tax Amount
     * @return {string} The Tax Amount
     */
    get taxAmount() {
        return this._taxAmount;
    }

    /**
     * Set the Tax Amount
     * @param {string} value - The Tax Amount
     */
    set taxAmount(value) {
        this._taxAmount = value;
    }

    /**
     * Get the material source
     * @return {LabelValue} The material source
     */
    get source() {
        return this._source;
    }

    /**
     * Set the material source
     * @param {LabelValue} value - The material source
     */
    set source(value) {
        this._source = value;
    }
}

